import { Continents, GameOptions, SubscriptionProduct } from "pages/productManagement/types";
import type { continentCountries } from "pages/productManagement/types";
import type { SubscriptionRowResponse, SubscriptionUpsertRequest } from "libs/apiClient";
import { type SubscriptionBehavior } from "./subscriptionProductConfig";

export interface SelectOption {
	value: string | number;
	label: string;
}

export interface CountrySelectOption extends SelectOption {
	value: string;
	continent: string;
}

export interface SubscriptionFormValues {
	id?: number | null;
	comment?: string | null;
	product?: SelectOption | null;
	maxAccounts?: number | string;
	startDate?: string | null;
	expirationDate?: string | null;
	pipedriveDealId?: string | null;
	countries?: CountrySelectOption[] | null;
	nationalCountries?: SelectOption[] | null;
	discoverCountries?: SelectOption[] | null;
	gameOptions?: string[] | null;
	proClub?: SelectOption | null;
	ownership?: SelectOption[] | null;
	teamGroupId?: SelectOption | null;
	eyeballAddons?: string[];
	genderOptions?: string[] | null;
}

function isSystemPopulatedComment(comment: string | null | undefined): boolean {
	if (comment == null || comment === "") return false;
	try {
		const o = JSON.parse(comment) as unknown;
		return typeof o === "object" && o !== null && "id" in o && "value" in o;
	} catch {
		return false;
	}
}

function extractKeyValueFromComment(comment: string | null | undefined): string | null {
	if (comment == null || comment === "") return null;
	try {
		const o = JSON.parse(comment) as { value?: string };
		return typeof o?.value === "string" ? o.value : null;
	} catch {
		return null;
	}
}

function booleanMapToFormKeys(api: Partial<Record<string, boolean>> | string[] | null | undefined): string[] {
	if (api == null) return [];
	if (Array.isArray(api)) {
		return api.filter((key): key is string => typeof key === "string");
	}
	if (typeof api !== "object") return [];
	return Object.entries(api)
		.filter(([, v]) => v)
		.map(([k]) => k);
}

function apiEyeballAddonsToFormValues(api: Partial<Record<string, boolean>> | string[] | null | undefined): string[] {
	return booleanMapToFormKeys(api);
}

function isCountriesByContinent(countries: unknown): countries is Record<string, string[]> {
	if (countries == null || typeof countries !== "object" || Array.isArray(countries)) {
		return false;
	}

	return Object.values(countries).every(
		(value) => Array.isArray(value) && value.every((item) => typeof item === "string"),
	);
}

function isCountrySelectOptionArray(countries: unknown): countries is CountrySelectOption[] {
	return (
		Array.isArray(countries) &&
		countries.every(
			(item) =>
				item != null &&
				typeof item === "object" &&
				"value" in item &&
				"continent" in item &&
				typeof item.value === "string" &&
				typeof item.continent === "string",
		)
	);
}

function apiCountriesToFormValues(
	countries: unknown,
	countriesWithContinents: continentCountries[],
): CountrySelectOption[] | null {
	if (countries == null) {
		return null;
	}

	if (isCountrySelectOptionArray(countries)) {
		return countries;
	}

	if (!isCountriesByContinent(countries)) {
		return null;
	}

	const countryLabelByValue = new Map<string, string>();
	for (const group of countriesWithContinents) {
		for (const option of group.options) {
			countryLabelByValue.set(option.value, option.label);
		}
	}

	const result: CountrySelectOption[] = [];
	for (const [continent, countryCodes] of Object.entries(countries)) {
		for (const code of countryCodes) {
			result.push({
				value: code,
				label: countryLabelByValue.get(code) ?? code,
				continent,
			});
		}
	}

	return result;
}

function isSelectOptionArray(values: unknown): values is SelectOption[] {
	return (
		Array.isArray(values) &&
		values.every(
			(item) =>
				item != null &&
				typeof item === "object" &&
				"value" in item &&
				"label" in item &&
				(item.value === null || typeof item.value === "string" || typeof item.value === "number") &&
				typeof item.label === "string",
		)
	);
}

function apiNationalCountriesToFormValues(
	nationalCountries: unknown,
	nationalContinentOptions: SelectOption[],
): SelectOption[] | null {
	if (nationalCountries == null) {
		return null;
	}

	if (isSelectOptionArray(nationalCountries)) {
		return nationalCountries;
	}

	if (typeof nationalCountries !== "object" || Array.isArray(nationalCountries)) {
		return null;
	}

	const continentOptionByValue = new Map(
		nationalContinentOptions.map((option) => [String(option.value), option]),
	);

	return Object.entries(nationalCountries)
		.filter(([, values]) => Array.isArray(values) && values.includes("all"))
		.map(([continent]) => continentOptionByValue.get(continent) ?? { value: continent, label: continent });
}

function apiDiscoverCountriesToFormValues(
	discoverCountries: unknown,
	discoverCountryOptions: SelectOption[],
): SelectOption[] | null {
	if (discoverCountries == null) {
		return null;
	}

	if (isSelectOptionArray(discoverCountries)) {
		return discoverCountries;
	}

	if (Array.isArray(discoverCountries)) {
		const optionByValue = new Map(
			discoverCountryOptions.map((option) => [String(option.value), option]),
		);

		return discoverCountries
			.filter((value): value is string => typeof value === "string" && value !== "")
			.map((value) => optionByValue.get(value) ?? { value, label: value });
	}

	return null;
}

function apiTeamGroupToFormValue(
	teamGroupId: unknown,
	teamGroupName: unknown,
	teamGroupOptions: SelectOption[],
): SelectOption | undefined {
	if (teamGroupId == null || teamGroupId === "") {
		return undefined;
	}

	const id = Number(teamGroupId);
	const option = teamGroupOptions.find((entry) => Number(entry.value) === id);
	if (option) {
		return option;
	}

	return {
		value: id,
		label: typeof teamGroupName === "string" && teamGroupName !== "" ? teamGroupName : String(id),
	};
}

function apiOwnershipsToFormValues(ownerships: unknown, ownershipOptions: SelectOption[]): SelectOption[] {
	if (ownerships == null) {
		return [];
	}

	if (isSelectOptionArray(ownerships)) {
		return ownerships;
	}

	if (!Array.isArray(ownerships)) {
		return [];
	}

	const optionByValue = new Map(ownershipOptions.map((option) => [Number(option.value), option]));

	return ownerships
		.map((ownership) => {
			if (ownership != null && typeof ownership === "object" && "value" in ownership) {
				const value = Number((ownership as SelectOption).value);
				return optionByValue.get(value) ?? (ownership as SelectOption);
			}

			const value = Number(ownership);
			return optionByValue.get(value) ?? { value, label: String(value) };
		})
		.filter((option): option is SelectOption => option != null);
}

export function buildSubscriptionInitialValues(
	row: SubscriptionRowResponse | undefined,
	options: {
		ownershipOptions?: SelectOption[];
		teamGroupOptions?: SelectOption[];
		countriesWithContinents?: continentCountries[];
		nationalContinentOptions?: SelectOption[];
		discoverCountryOptions?: SelectOption[];
	},
): SubscriptionFormValues {
	const discoverCountries = row
		? apiDiscoverCountriesToFormValues(row.discoverCountries, options.discoverCountryOptions ?? [])
		: null;

	if (!row) {
		return {
			comment: null,
			product: null,
			maxAccounts: 0,
			startDate: null,
			expirationDate: null,
			pipedriveDealId: null,
			countries: null,
			nationalCountries: null,
			discoverCountries: null,
			gameOptions: null,
			proClub: { value: "", label: "" },
			ownership: options.ownershipOptions?.length ? [options.ownershipOptions[0]] : [],
			teamGroupId: undefined,
			eyeballAddons: [],
			genderOptions: null,
		};
	}

	const comment =
		row.product === SubscriptionProduct.CUSTOMER_API && isSystemPopulatedComment(row.comment)
			? extractKeyValueFromComment(row.comment) ?? row.comment ?? null
			: row.comment ?? null;

	return {
		id: row.id,
		comment,
		product: row.product ? { value: row.product, label: row.product } : null,
		maxAccounts: row.maxAccounts ?? 0,
		startDate: row.startDate ?? null,
		expirationDate: row.expirationDate ?? null,
		pipedriveDealId: row.pipedriveDealId ?? null,
		countries: apiCountriesToFormValues(row.countries, options.countriesWithContinents ?? []),
		nationalCountries: apiNationalCountriesToFormValues(
			row.nationalCountries,
			options.nationalContinentOptions ?? [],
		),
		discoverCountries,
		gameOptions: booleanMapToFormKeys(row.gameOptions ?? undefined),
		proClub:
			row.proClubId != null
				? { value: row.proClubId, label: row.proClubName ?? String(row.proClubId) }
				: { value: "", label: "" },
		ownership: apiOwnershipsToFormValues(row.ownerships, options.ownershipOptions ?? []),
		teamGroupId: apiTeamGroupToFormValue(
			row.teamGroupId ?? (row as { teamGroup?: number | null }).teamGroup,
			row.teamGroupName,
			options.teamGroupOptions ?? [],
		),
		eyeballAddons: apiEyeballAddonsToFormValues(row.eyeballAddons ?? undefined),
		genderOptions: booleanMapToFormKeys(row.genderOptions ?? undefined),
	};
}

function resolveSelectedOptionKeys(
	formKeys: string[] | null | undefined,
	existingValue: Partial<Record<string, boolean>> | string[] | null | undefined,
): string[] {
	if (formKeys != null) {
		return formKeys;
	}

	return booleanMapToFormKeys(existingValue ?? undefined);
}

function resolveSelectedProClubId(value: SelectOption["value"] | null | undefined): number | null {
	if (value == null || value === "") {
		return null;
	}

	const proClubId = Number(value);
	if (Number.isFinite(proClubId) === false) {
		return null;
	}

	return proClubId;
}

function mapOwnershipOptionsToPayload(
	ownershipOptions: SelectOption[],
): NonNullable<SubscriptionUpsertRequest["ownership"]> {
	return ownershipOptions.map((ownershipOption) => ({
		value: ownershipOption.value,
		label: ownershipOption.label,
	}));
}

export function buildSubscriptionPayload(
	values: SubscriptionFormValues,
	behavior: SubscriptionBehavior,
	existingRow: SubscriptionRowResponse | undefined,
	eyeballAddonKeys: string[],
): SubscriptionUpsertPayloadResult {
	const product = values.product?.value ? String(values.product.value) : null;
	if (!product) {
		return { ok: false, errorKey: "message.select_product" };
	}

	const selectedProClubId = resolveSelectedProClubId(values.proClub?.value);
	const canSaveWithoutProClub = existingRow !== undefined && existingRow.proClubId == null;
	if (selectedProClubId === null && canSaveWithoutProClub === false) {
		return { ok: false, errorKey: "app.player.register.club.select" };
	}

	const countries: Record<string, string[]> = {
		[Continents.AFRICA]: [],
		[Continents.ASIA]: [],
		[Continents.NORTH_AMERICA]: [],
		[Continents.SOUTH_AMERICA]: [],
		[Continents.OCEANIA]: [],
		[Continents.EUROPE]: [],
	};

	const nationalCountries: Record<string, string[]> = {
		[Continents.AFRICA]: [],
		[Continents.ASIA]: [],
		[Continents.NORTH_AMERICA]: [],
		[Continents.SOUTH_AMERICA]: [],
		[Continents.OCEANIA]: [],
		[Continents.EUROPE]: [],
	};

	values.countries?.forEach((entry) => {
		if (!countries[entry.continent]) {
			countries[entry.continent] = [];
		}
		countries[entry.continent].push(String(entry.value));
	});

	values.nationalCountries?.forEach((entry) => {
		const continentKey = String(entry.value);
		if (!nationalCountries[continentKey]) {
			nationalCountries[continentKey] = [];
		}
		nationalCountries[continentKey].push("all");
	});

	if (
		behavior.hasCountries &&
		(!values.countries || values.countries.length === 0) &&
		(!values.nationalCountries || values.nationalCountries.length === 0)
	) {
		return { ok: false, errorKey: "message.select_country" };
	}

	const gameOptions: Record<string, boolean> = {
		[GameOptions.AI]: false,
		[GameOptions.STATISTICS_RATING]: false,
		[GameOptions.STATISTICS_PLAYERS]: false,
		[GameOptions.STATISTICS_TEAMS]: false,
		[GameOptions.EVENTS]: false,
		[GameOptions.DOWNLOADABLE]: false,
		[GameOptions.CLIPS]: false,
		[GameOptions.DOWNLOADABLE_GAMETAGS]: false,
		[GameOptions.PLAYER_STATS_SEARCH]: false,
	};

	if (behavior.hasGameOptions) {
		const validGameOptionKeys = new Set<string>(Object.values(GameOptions));
		const selectedGameOptions = resolveSelectedOptionKeys(
			values.gameOptions,
			existingRow?.gameOptions ?? undefined,
		).filter((key) => validGameOptionKeys.has(key));
		if (behavior.hasGameOptionsRequired && selectedGameOptions.length === 0) {
			return { ok: false, errorKey: "message.select_game_options" };
		}
		selectedGameOptions.forEach((key) => {
			gameOptions[key] = true;
		});
	}

	const genderOptionsPayload: Record<string, boolean> = {};
	if (behavior.hasGenderOptions) {
		const selectedGenderOptions = resolveSelectedOptionKeys(values.genderOptions, existingRow?.genderOptions ?? undefined);
		if (selectedGenderOptions.length === 0) {
			return { ok: false, errorKey: "message.select_gender_options" };
		}
		selectedGenderOptions.forEach((key) => {
			genderOptionsPayload[key] = true;
		});
	}

	let eyeballAddonsObj: Record<string, boolean> | undefined;
	if (behavior.hasAddons) {
		const selectedEyeballAddons = resolveSelectedOptionKeys(
			values.eyeballAddons,
			existingRow?.eyeballAddons ?? undefined,
		);
		if (behavior.hasAddonsRequired && selectedEyeballAddons.length === 0) {
			return { ok: false, errorKey: "message.select_eyeball_addons" };
		}
		const initialKeys =
			existingRow?.eyeballAddons && typeof existingRow.eyeballAddons === "object" && !Array.isArray(existingRow.eyeballAddons)
				? Object.keys(existingRow.eyeballAddons)
				: eyeballAddonKeys;
		eyeballAddonsObj = Object.fromEntries(initialKeys.map((k) => [k, false]));
		selectedEyeballAddons.forEach((key) => {
			eyeballAddonsObj![key] = true;
		});
	}

	if (behavior.hasTeamGroup && values.teamGroupId?.value == null) {
		return { ok: false, errorKey: "Please select team group" };
	}

	const payload: SubscriptionUpsertRequest = {
		comment: values.comment ?? null,
		product,
		maxAccounts: values.maxAccounts ? parseInt(String(values.maxAccounts), 10) : 0,
		startDate: values.startDate ?? null,
		expirationDate: values.expirationDate ?? null,
		proClub: selectedProClubId,
		pipedriveDealId: values.pipedriveDealId ?? null,
	};

	if (behavior.hasCountries || behavior.hasNationalContinents) {
		payload.countries = countries;
		payload.nationalCountries = nationalCountries;
	}

	if (behavior.hasDiscoverCountries) {
		payload.discoverCountries =
			values.discoverCountries?.map((entry) => String(entry.value)) ?? [];
	}

	if (behavior.hasGameOptions) {
		payload.gameOptions = gameOptions;
	}
	if (behavior.hasGenderOptions) {
		payload.genderOptions = genderOptionsPayload;
	}
	if (behavior.hasAddons && eyeballAddonsObj) {
		payload.eyeballAddons = eyeballAddonsObj;
	}
	if (behavior.hasOwnerships && values.ownership) {
		payload.ownership = mapOwnershipOptionsToPayload(values.ownership);
	}
	if (behavior.hasTeamGroup && values.teamGroupId?.value != null) {
		payload.teamGroupId = Number(values.teamGroupId.value);
	}

	return { ok: true, payload };
}

type SubscriptionUpsertPayloadResult =
	| { ok: true; payload: SubscriptionUpsertRequest }
	| { ok: false; errorKey: string };
